import React, { useState, useEffect } from 'react';
import { Search, Filter, Star, Globe, Lock, Trash2, MoreHorizontal, Users, BarChart3, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';

const AdminCommunities = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
      return;
    }
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const data = await api.get('/public/communities');
      setCommunities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch communities', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = async (id, currentFeatured) => {
    try {
      await api.put(`/communities/${id}`, { is_featured: !currentFeatured });
      setCommunities(prev => Array.isArray(prev) ? prev.map(c => c.id === id ? { ...c, is_featured: !currentFeatured } : c) : []);
    } catch (err) {
      console.error('Failed to toggle featured status', err);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/communities/${id}`, { status });
      setCommunities(prev => Array.isArray(prev) ? prev.map(c => c.id === id ? { ...c, status } : c) : []);
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const filteredCommunities = (Array.isArray(communities) ? communities : []).filter(c => 
    (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (c.tagline && c.tagline.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Community Management</h1>
          <p className="text-gray-500 font-medium mt-1">Manage, verify, and feature communities across the platform.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search communities..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-brand-indigo/10 outline-none w-64"
            />
          </div>
          <button className="p-2 bg-white border border-gray-100 rounded-xl text-gray-600 hover:bg-gray-50 transition-all">
            <Filter size={20} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-50">
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Community</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Type</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Members</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Status</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Featured</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredCommunities.map((community) => (
              <tr key={community.id} className="hover:bg-gray-50/30 transition-colors group">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-3">
                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-sm", community.color)}>
                      {community.name[0]}
                    </div>
                    <p className="font-bold text-gray-900 text-sm">{community.name}</p>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500">
                    {community.type === 'Public' ? <Globe size={14} /> : <Lock size={14} />}
                    {community.type}
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-1.5 text-sm font-bold text-gray-900">
                    <Users size={14} className="text-gray-400" />
                    {community.members}
                  </div>
                </td>
                <td className="px-8 py-5">
                  <span className={cn(
                    "text-xxs font-bold px-2 py-1 rounded-lg uppercase tracking-[0.15em]",
                    community.status === 'Active' ? "text-green-600 bg-green-50" : 
                    community.status === 'Pending' ? "text-orange-600 bg-orange-50" : "text-red-600 bg-red-50"
                  )}>
                    {community.status}
                  </span>
                </td>
                <td className="px-8 py-5">
                  <button 
                    onClick={() => toggleFeatured(community.id, community.is_featured)}
                    className={cn(
                      "p-2 rounded-xl transition-all active:scale-90",
                      community.is_featured ? "text-yellow-500 bg-yellow-50" : "text-gray-300 hover:text-yellow-500 hover:bg-yellow-50"
                    )}
                  >
                    <Star size={18} fill={community.is_featured ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => updateStatus(community.id, 'Active')}
                      className="p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all active:scale-90" 
                      title="Verify/Activate"
                    >
                      <ShieldCheck size={18} />
                    </button>
                    <button 
                      onClick={() => updateStatus(community.id, 'Suspended')}
                      className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all active:scale-90" 
                      title="Suspend"
                    >
                      <Trash2 size={18} />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-900 rounded-xl">
                      <MoreHorizontal size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCommunities;
